import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { clientsAPI, vehiclesAPI, devicesAPI, documentsAPI, attestationVehiclesAPI } from '../services/api';

function AttestationCreationModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [devices, setDevices] = useState([]);
  
  const [formData, setFormData] = useState({
    client: null,
    document_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [currentDevice, setCurrentDevice] = useState(null);
  
  // Track if data has been loaded to prevent duplicate requests
  const dataLoadedRef = useRef(false);
  const devicesRef = useRef([]);

  useEffect(() => {
    if (open && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      loadInitialData();
    }
    if (!open) {
      dataLoadedRef.current = false;
    }
  }, [open]);

  const loadInitialData = async () => {
    try {
      const [clientsRes, devicesRes] = await Promise.all([
        clientsAPI.getAll(),
        devicesAPI.getAllBulk(),
      ]);
      setClients(clientsRes.data);
      setDevices(devicesRes.data);
      devicesRef.current = devicesRes.data;
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Erreur lors du chargement des données');
    }
  };

  useEffect(() => {
    if (formData.client && devicesRef.current.length > 0) {
      loadClientVehicles(formData.client.id);
    } else if (!formData.client) {
      setVehicles([]);
      setSelectedVehicles([]);
    }
  }, [formData.client]);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Erreur lors du chargement des clients');
    }
  };

  const loadClientVehicles = async (clientId) => {
    try {
      const response = await vehiclesAPI.getByClient(clientId);
      const clientVehicles = response.data;
      setVehicles(clientVehicles);
      
      // Automatically add all vehicles with their current devices
      const autoSelectedVehicles = [];
      let lineNumber = 1;
      const devicesList = devicesRef.current;
      
      for (const vehicle of clientVehicles) {
        // Find the device associated with this vehicle
        const device = vehicle.device_imei 
          ? devicesList.find(d => d.imei === vehicle.device_imei)
          : null;
        
        if (device) {
          autoSelectedVehicles.push({
            vehicle: vehicle,
            device: device,
            line_number: lineNumber++,
          });
        }
      }
      
      setSelectedVehicles(autoSelectedVehicles);
      
      if (autoSelectedVehicles.length > 0) {
        setError(null);
      } else {
        setError('Aucun véhicule avec appareil GPS trouvé pour ce client');
      }
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Erreur lors du chargement des véhicules');
    }
  };

  const loadDevices = async () => {
    try {
      const response = await devicesAPI.getAllBulk();
      setDevices(response.data);
    } catch (err) {
      console.error('Error loading devices:', err);
      setError('Erreur lors du chargement des appareils');
    }
  };

  const handleAddVehicle = () => {
    if (!currentVehicle || !currentDevice) {
      setError('Veuillez sélectionner un véhicule et un appareil');
      return;
    }

    // Check if vehicle already added
    if (selectedVehicles.find(v => v.vehicle.id === currentVehicle.id)) {
      setError('Ce véhicule est déjà ajouté');
      return;
    }

    setSelectedVehicles([
      ...selectedVehicles,
      {
        vehicle: currentVehicle,
        device: currentDevice,
        line_number: selectedVehicles.length + 1,
      }
    ]);

    setCurrentVehicle(null);
    setCurrentDevice(null);
    setError(null);
  };

  const handleRemoveVehicle = (index) => {
    const updated = selectedVehicles.filter((_, i) => i !== index);
    // Renumber
    updated.forEach((item, i) => {
      item.line_number = i + 1;
    });
    setSelectedVehicles(updated);
  };

  const handleSubmit = async () => {
    // Prevent double submission
    if (loading) return;
    
    if (!formData.client) {
      setError('Veuillez sélectionner un client');
      return;
    }

    if (selectedVehicles.length === 0) {
      setError('Veuillez ajouter au moins un véhicule');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create document
      const documentData = {
        document_type: 'attestation',
        client: formData.client.id,
        document_date: formData.document_date,
        notes: formData.notes,
      };

      const docResponse = await documentsAPI.create(documentData);
      const documentId = docResponse.data.id;

      // Create attestation vehicles in batch using Promise.all
      const vehiclePromises = selectedVehicles.map(item => 
        attestationVehiclesAPI.create({
          document: documentId,
          vehicle: item.vehicle.id,
          device: item.device.id,
          line_number: item.line_number,
        })
      );
      await Promise.all(vehiclePromises);

      // Generate PDF
      await documentsAPI.generatePDF(documentId);

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (err) {
      console.error('Error creating attestation:', err);
      setError('Erreur lors de la création de l\'attestation: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      client: null,
      document_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setSelectedVehicles([]);
    setCurrentVehicle(null);
    setCurrentDevice(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6">Créer une Attestation</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Client Selection */}
          <Autocomplete
            options={clients}
            getOptionLabel={(option) => `${option.company_name} (${option.client_code})`}
            value={formData.client}
            onChange={(e, newValue) => {
              setFormData({ ...formData, client: newValue });
              setSelectedVehicles([]);
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span>{option.company_name} ({option.client_code})</span>
                {option.server_name && (
                  <span style={{ 
                    backgroundColor: '#e3f2fd', 
                    color: '#1976d2', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.75em',
                  }}>
                    🔗 {option.server_name}
                  </span>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Client *" required />
            )}
            componentsProps={{
              paper: {
                sx: { minWidth: 400 }
              }
            }}
          />

          {/* Document Date */}
          <TextField
            label="Date du document"
            type="date"
            value={formData.document_date}
            onChange={(e) => setFormData({ ...formData, document_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {/* Notes */}
          <TextField
            label="Notes"
            multiline
            rows={2}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
          />

          {/* Vehicle Selection Section */}
          {formData.client && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Véhicules à inclure
                </Typography>
                <Chip 
                  label={`${selectedVehicles.length} véhicule(s) sélectionné(s)`}
                  color="primary"
                  size="small"
                />
              </Box>

              <Alert severity="info" sx={{ mt: 1 }}>
                Les véhicules du client avec leurs appareils GPS ont été automatiquement ajoutés. 
                Vous pouvez ajouter ou retirer des véhicules ci-dessous.
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mt: 2 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={vehicles}
                  getOptionLabel={(option) => `${option.registration_number} - ${option.brand} ${option.model}`}
                  value={currentVehicle}
                  onChange={(e, newValue) => setCurrentVehicle(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Ajouter un véhicule" size="small" />
                  )}
                />

                <Autocomplete
                  sx={{ flex: 1 }}
                  options={devices}
                  getOptionLabel={(option) => `${option.imei} (${option.model || 'N/A'})`}
                  value={currentDevice}
                  onChange={(e, newValue) => setCurrentDevice(newValue)}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <span>{option.imei} ({option.model || 'N/A'})</span>
                      {option.server_name && (
                        <span style={{ 
                          backgroundColor: '#e3f2fd', 
                          color: '#1976d2', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '0.75em',
                        }}>
                          🔗 {option.server_name}
                        </span>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Appareil GPS" size="small" />
                  )}
                  componentsProps={{
                    paper: {
                      sx: { minWidth: 400 }
                    }
                  }}
                />

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddVehicle}
                  disabled={!currentVehicle || !currentDevice}
                >
                  Ajouter
                </Button>
              </Box>

              {/* Selected Vehicles Table */}
              {selectedVehicles.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>N°</strong></TableCell>
                        <TableCell><strong>Immatriculation</strong></TableCell>
                        <TableCell><strong>Nom du Véhicule</strong></TableCell>
                        <TableCell><strong>IMEI GPS</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedVehicles.map((item, index) => {
                        // Get vehicle display name
                        const vehicleName = item.vehicle.name || 
                          (item.vehicle.brand || item.vehicle.model 
                            ? `${item.vehicle.brand || ''} ${item.vehicle.model || ''}`.trim()
                            : 'N/A');
                        
                        return (
                          <TableRow key={`vehicle-${item.vehicle.id}-${item.device.id}`}>
                            <TableCell>{item.line_number}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.vehicle.registration_number || 'N/A'} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>{vehicleName}</TableCell>
                            <TableCell>{item.device.imei}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveVehicle(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {selectedVehicles.length === 0 && (
                <Alert severity="warning">
                  Aucun véhicule avec appareil GPS trouvé pour ce client. 
                  Veuillez ajouter manuellement des véhicules avec leurs appareils.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.client || selectedVehicles.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <DescriptionIcon />}
        >
          {loading ? 'Création...' : 'Créer l\'Attestation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AttestationCreationModal;
